import type { ApiClient } from '@lista/api-client';
import type {
  AddListItemRequest,
  BatchAddListItemsRequest,
  CatalogItemDto,
  CategoryDto,
  CuisineDto,
  CreateListRequest,
  FavoriteDto,
  InvitationDto,
  ListItem,
  ListSummary,
  LoginRequest,
  LoginResponse,
  MealDetail,
  MealSummary,
  MemberDto,
  NotificationsResponse,
  PdfLinkResponse,
  PendingShare,
  RegisterRequest,
  ReplaceListItemRequest,
  RecentItemDto,
  ReplaceListRequest,
  PublicList,
  SearchResult,
  ShareLink,
  ShareListRequest,
  UpdateProfileRequest,
  UserProfile,
  UserDataExport,
  AdminCatalogItem,
  AdminCatalogQuery,
  CreateCatalogItemRequest,
  CustomName,
  CustomNamesQuery,
  NumberedPage,
  PromoteCustomNameRequest,
  PromoteCustomNameResponse,
  UpdateCatalogItemRequest,
  AdminSponsoredList,
  SponsoredList,
  SponsoredListInput,
  SuggestItemsRequest,
  AiCapabilities,
  ScanListRequest,
  ScanListResponse,
  SuggestItemsResponse,
} from '@lista/shared-validation';
import { ifMatch, unwrap, unwrapEmpty } from './http';
import type { Neighbours } from './ordering';

/** How many items each list card previews; apps set it from their env (default 4). */
let listPreviewCount = 4;
export function setListPreviewCount(count: number): void {
  if (Number.isFinite(count)) listPreviewCount = Math.min(10, Math.max(0, Math.trunc(count)));
}

export interface DashboardFilters {
  ownership?: 'all' | 'owned' | 'shared';
  status?: 'active' | 'archived' | 'deleted';
  sort?: 'custom' | 'name' | 'updatedAt' | 'createdAt';
}

export interface Page<T> {
  data: T[];
  page: { nextCursor: string | null; hasMore: boolean };
}

export interface SearchParams {
  q: string;
  sources?: Array<'catalog' | 'custom' | 'favorites' | 'categories'>;
  categoryId?: string;
  listId?: string;
  limit?: number;
}

export interface MembersOverview {
  members: MemberDto[];
  invitations: InvitationDto[];
}

export interface PdfOptions {
  includePurchased?: boolean;
  groupByCategory?: boolean;
  paper?: 'A4' | 'LETTER';
  locale?: 'en' | 'ar';
  timeZone?: string;
}

export interface PdfQuery {
  includePurchased: 'true' | 'false';
  groupByCategory: 'true' | 'false';
  paper: 'A4' | 'LETTER';
  locale: 'en' | 'ar';
  timeZone: string;
}

/** Query string for GET /lists/{id}/pdf and POST /lists/{id}/pdf/link. */
export function pdfQuery(o: PdfOptions): PdfQuery {
  return {
    includePurchased: o.includePurchased === false ? 'false' : 'true',
    groupByCategory: o.groupByCategory === false ? 'false' : 'true',
    paper: o.paper ?? 'A4',
    locale: o.locale ?? 'en',
    timeZone: o.timeZone ?? 'UTC',
  };
}

/** RFC 6266 `filename*` first, then the ASCII fallback. */
export function fileNameFromDisposition(
  header: string | null | undefined,
  fallback: string,
): string {
  const star = header?.match(/filename\*=UTF-8''([^;]+)/i)?.[1];
  if (star) return decodeURIComponent(star);
  return header?.match(/filename="([^"]+)"/i)?.[1] ?? fallback;
}

/**
 * Every REST call the apps make, typed from the OpenAPI contract. One instance per app,
 * built over that app's configured client (auth adapter, base URL).
 */
export function createServices(api: ApiClient) {
  const auth = {
    login: (body: LoginRequest): Promise<LoginResponse> =>
      unwrap(api.POST('/auth/login', { body })),
    register: (body: RegisterRequest) => unwrap(api.POST('/auth/register', { body })),
    logout: (refreshToken?: string) =>
      unwrapEmpty(api.POST('/auth/logout', { body: refreshToken ? { refreshToken } : {} })),
    logoutAll: () => unwrapEmpty(api.POST('/auth/logout-all')),
    /** Signs out every other session; the response is this device's new session. */
    changePassword: (body: {
      currentPassword: string;
      newPassword: string;
    }): Promise<LoginResponse> => unwrap(api.POST('/auth/change-password', { body })),
    forgotPassword: (email: string) =>
      unwrap(api.POST('/auth/forgot-password', { body: { email } })),
    resetPassword: (token: string, newPassword: string) =>
      unwrapEmpty(api.POST('/auth/reset-password', { body: { token, newPassword } })),
    verifyEmail: (token: string) =>
      unwrapEmpty(api.POST('/auth/verify-email', { body: { token } })),
    /** Profile: email a fresh verification link to the signed-in user. */
    resendVerification: () => unwrapEmpty(api.POST('/auth/resend-verification')),
  };

  const users = {
    me: (): Promise<UserProfile> => unwrap(api.GET('/users/me')),
    update: (body: UpdateProfileRequest): Promise<UserProfile> =>
      unwrap(api.PATCH('/users/me', { body })),
    /** UC-ACC-09: irreversible; the server also ends the session. */
    deleteAccount: (password: string): Promise<void> =>
      unwrapEmpty(api.DELETE('/users/me', { body: { password } })),
    /** FR-USR-03: everything held about the caller, as the JSON document to save. */
    exportData: (): Promise<UserDataExport> => unwrap(api.GET('/users/me/export')),
  };

  const lists = {
    dashboard: (filters: DashboardFilters, cursor?: string): Promise<Page<ListSummary>> =>
      unwrap(
        api.GET('/lists', {
          params: { query: { ...filters, limit: 50, cursor, previewItems: listPreviewCount } },
        }),
      ),
    get: (id: string): Promise<ListSummary> =>
      unwrap(api.GET('/lists/{id}', { params: { path: { id } } })),
    create: (body: CreateListRequest): Promise<ListSummary> => unwrap(api.POST('/lists', { body })),
    /** Full replace guarded by the version the user saw (412 if someone changed it). */
    replace: (
      list: Pick<ListSummary, 'id' | 'version'>,
      body: ReplaceListRequest,
    ): Promise<ListSummary> =>
      unwrap(
        api.PUT('/lists/{id}', {
          params: { path: { id: list.id }, header: ifMatch(list.version) },
          body,
        }),
      ),
    remove: (id: string) => unwrapEmpty(api.DELETE('/lists/{id}', { params: { path: { id } } })),
    /** Personal dashboard order (FR-LST-06): place the list between two neighbours. */
    move: (id: string, neighbours: Neighbours) =>
      unwrapEmpty(api.PUT('/lists/{id}/position', { params: { path: { id } }, body: neighbours })),
    /** Personal pin: pinned lists come first on your dashboards. */
    pin: (id: string, pinned: boolean) =>
      unwrapEmpty(api.PUT('/lists/{id}/pin', { params: { path: { id } }, body: { pinned } })),
    /** Renders the PDF server-side and returns a short-lived download link (FR-EXP, shareable). */
    pdfLink: (id: string, options: PdfOptions = {}): Promise<PdfLinkResponse> =>
      unwrap(
        api.POST('/lists/{id}/pdf/link', {
          params: { path: { id }, query: pdfQuery(options) },
        }),
      ),
  };

  const listItems = {
    list: async (listId: string): Promise<ListItem[]> =>
      (await unwrap(api.GET('/lists/{id}/items', { params: { path: { id: listId } } }))).data,
    add: (listId: string, body: Partial<AddListItemRequest>): Promise<ListItem> =>
      unwrap(
        api.POST('/lists/{id}/items', {
          params: { path: { id: listId } },
          body: { quantity: '1', mode: 'merge', ...body } as AddListItemRequest,
        }),
      ),
    /** Several products at once (all or nothing), e.g. the ingredients of a meal. */
    addMany: async (
      listId: string,
      items: Array<Partial<AddListItemRequest>>,
    ): Promise<ListItem[]> =>
      (
        await unwrap(
          api.POST('/lists/{id}/items/batch', {
            params: { path: { id: listId } },
            body: {
              items: items.map((i) => ({ quantity: '1', mode: 'merge', ...i })),
            } as BatchAddListItemsRequest,
          }),
        )
      ).data,
    replace: (listId: string, itemId: string, body: ReplaceListItemRequest): Promise<ListItem> =>
      unwrap(
        api.PUT('/lists/{id}/items/{listItemId}', {
          params: { path: { id: listId, listItemId: itemId } },
          body,
        }),
      ),
    remove: (listId: string, itemId: string) =>
      unwrapEmpty(
        api.DELETE('/lists/{id}/items/{listItemId}', {
          params: { path: { id: listId, listItemId: itemId } },
        }),
      ),
  };

  const catalog = {
    categories: async (): Promise<CategoryDto[]> =>
      (await unwrap(api.GET('/categories', { params: { query: { limit: 200 } } }))).data,
    category: (id: string): Promise<CategoryDto> =>
      unwrap(api.GET('/categories/{id}', { params: { path: { id } } })),
    browse: (
      query: { categoryId?: string; sort?: 'name:asc' | 'name:desc' | 'category' },
      cursor?: string,
    ): Promise<Page<CatalogItemDto>> =>
      unwrap(api.GET('/items', { params: { query: { ...query, limit: 100, cursor } } })),
    /** The caller's recently used products, newest first (BR-REC-01..05). */
    recent: async (limit = 20): Promise<RecentItemDto[]> =>
      (await unwrap(api.GET('/items/recent', { params: { query: { limit } } }))).data,
    item: (id: string): Promise<CatalogItemDto> =>
      unwrap(api.GET('/items/{id}', { params: { path: { id } } })),
    search: async (p: SearchParams, signal?: AbortSignal): Promise<SearchResult[]> =>
      (
        await unwrap(
          api.GET('/items/search', {
            params: {
              query: {
                q: p.q,
                sources: p.sources?.join(','),
                categoryId: p.categoryId,
                listId: p.listId,
                limit: p.limit ?? 30,
              },
            },
            signal,
          }),
        )
      ).data,
  };

  const favorites = {
    /** All favorites (≤ 1000 per user, BR-FAV-04), fetched page by page. */
    all: async (): Promise<FavoriteDto[]> => {
      const out: FavoriteDto[] = [];
      let cursor: string | undefined;
      do {
        const page = await unwrap(
          api.GET('/favorites', { params: { query: { sort: 'category', limit: 200, cursor } } }),
        );
        out.push(...page.data);
        cursor = page.page.nextCursor ?? undefined;
      } while (cursor);
      return out;
    },
    add: (itemId: string): Promise<FavoriteDto> =>
      unwrap(api.POST('/favorites/{itemId}', { params: { path: { itemId } } })),
    remove: (itemId: string) =>
      unwrapEmpty(api.DELETE('/favorites/{itemId}', { params: { path: { itemId } } })),
  };

  const sharing = {
    members: (listId: string): Promise<MembersOverview> =>
      unwrap(api.GET('/lists/{id}/members', { params: { path: { id: listId } } })),
    add: (listId: string, body: ShareListRequest) =>
      unwrap(api.POST('/lists/{id}/members', { params: { path: { id: listId } }, body })),
    changeRole: (listId: string, userId: string, role: 'EDITOR' | 'VIEWER'): Promise<MemberDto> =>
      unwrap(
        api.PUT('/lists/{id}/members/{userId}', {
          params: { path: { id: listId, userId } },
          body: { role },
        }),
      ),
    remove: (listId: string, userId: string) =>
      unwrapEmpty(
        api.DELETE('/lists/{id}/members/{userId}', { params: { path: { id: listId, userId } } }),
      ),
    revokeInvitation: (listId: string, invitationId: string) =>
      unwrapEmpty(
        api.DELETE('/lists/{id}/invitations/{invitationId}', {
          params: { path: { id: listId, invitationId } },
        }),
      ),
    shareLinks: async (listId: string): Promise<ShareLink[]> =>
      (await unwrap(api.GET('/lists/{id}/share-links', { params: { path: { id: listId } } })))
        .data as ShareLink[],
    createShareLink: (listId: string, expiresInDays: number): Promise<ShareLink> =>
      unwrap(
        api.POST('/lists/{id}/share-links', {
          params: { path: { id: listId } },
          body: { expiresInDays },
        }),
      ) as Promise<ShareLink>,
    revokeShareLink: (listId: string, linkId: string) =>
      unwrapEmpty(
        api.DELETE('/lists/{id}/share-links/{linkId}', {
          params: { path: { id: listId, linkId } },
        }),
      ),
    /** Anonymous read of a shared list (the token is the credential). */
    publicList: (token: string): Promise<PublicList> =>
      unwrap(
        api.GET('/public/lists/{token}', { params: { path: { token } } }),
      ) as Promise<PublicList>,
    accept: (token: string): Promise<{ listId: string; role: string }> =>
      unwrap(api.POST('/invitations/{token}/accept', { params: { path: { token } } })),
    /** Lists shared with me that wait for my acceptance. */
    pending: async (): Promise<PendingShare[]> =>
      (await unwrap(api.GET('/shares/pending', {}))).data as PendingShare[],
    declineShare: (listId: string) =>
      unwrapEmpty(api.POST('/shares/{listId}/decline', { params: { path: { listId } } })),
    acceptShare: (listId: string): Promise<{ listId: string; role: string }> =>
      unwrap(api.POST('/shares/{listId}/accept', { params: { path: { listId } } })),
  };

  const notifications = {
    list: (unreadOnly = false): Promise<NotificationsResponse> =>
      unwrap(
        api.GET('/notifications', {
          params: { query: { limit: 50, ...(unreadOnly ? { unread: 'true' as const } : {}) } },
        }),
      ) as Promise<NotificationsResponse>,
    markRead: (id: string) =>
      unwrapEmpty(api.POST('/notifications/{id}/read', { params: { path: { id } } })),
    markAllRead: () => unwrapEmpty(api.POST('/notifications/read-all', {})),
  };

  const meals = {
    cuisines: async (): Promise<CuisineDto[]> => (await unwrap(api.GET('/cuisines', {}))).data,
    cuisine: (id: string): Promise<CuisineDto> =>
      unwrap(api.GET('/cuisines/{id}', { params: { path: { id } } })),
    mealsOf: async (cuisineId: string): Promise<MealSummary[]> =>
      (await unwrap(api.GET('/cuisines/{id}/meals', { params: { path: { id: cuisineId } } }))).data,
    meal: (id: string): Promise<MealDetail> =>
      unwrap(api.GET('/meals/{id}', { params: { path: { id } } })),
  };

  const ai = {
    suggest: (body: SuggestItemsRequest): Promise<SuggestItemsResponse> =>
      unwrap(api.POST('/ai/list-suggestions', { body })),
    capabilities: (): Promise<AiCapabilities> => unwrap(api.GET('/ai/capabilities')),
    /** ADR-0022: read a photo of a paper list; proposes lines, saves nothing. */
    scanList: (body: ScanListRequest): Promise<ScanListResponse> =>
      unwrap(api.POST('/ai/list-scans', { body })),
  };

  /** Catalog administration (permission catalog.manage, ADR-0023). */
  const catalogAdmin = {
    items: (query: Partial<AdminCatalogQuery>): Promise<NumberedPage<AdminCatalogItem>> =>
      unwrap(api.GET('/admin/catalog/items', { params: { query: query as never } })),
    create: (body: CreateCatalogItemRequest): Promise<AdminCatalogItem> =>
      unwrap(api.POST('/admin/catalog/items', { body })),
    update: (id: string, body: UpdateCatalogItemRequest): Promise<AdminCatalogItem> =>
      unwrap(api.PATCH('/admin/catalog/items/{id}', { params: { path: { id } }, body })),
    customNames: (query: Partial<CustomNamesQuery>): Promise<NumberedPage<CustomName>> =>
      unwrap(api.GET('/admin/catalog/custom-names', { params: { query: query as never } })),
    promote: (body: PromoteCustomNameRequest): Promise<PromoteCustomNameResponse> =>
      unwrap(api.POST('/admin/catalog/custom-names/promote', { body })),
  };

  /** Sponsored lists: users read what targets their country/city; admins manage them. */
  const sponsored = {
    list: async (): Promise<SponsoredList[]> => (await unwrap(api.GET('/sponsored-lists'))).data,
    get: (id: string): Promise<SponsoredList> =>
      unwrap(api.GET('/sponsored-lists/{id}', { params: { path: { id } } })),
    admin: {
      list: async (): Promise<AdminSponsoredList[]> =>
        (await unwrap(api.GET('/admin/sponsored-lists'))).data,
      create: (body: SponsoredListInput): Promise<AdminSponsoredList> =>
        unwrap(api.POST('/admin/sponsored-lists', { body })),
      replace: (id: string, body: SponsoredListInput): Promise<AdminSponsoredList> =>
        unwrap(api.PUT('/admin/sponsored-lists/{id}', { params: { path: { id } }, body })),
      remove: (id: string) =>
        unwrapEmpty(api.DELETE('/admin/sponsored-lists/{id}', { params: { path: { id } } })),
    },
  };

  return {
    auth,
    users,
    lists,
    listItems,
    catalog,
    favorites,
    sharing,
    catalogAdmin,
    notifications,
    meals,
    ai,
    sponsored,
  };
}

export type Services = ReturnType<typeof createServices>;
